import { adaptProductForTracking, NormalizedTrackingProduct } from "./itemDataTransformer"
import {
    generateProductKeyFromTrackingData,
    getProductOrigin
} from "./productTrackingStore"

interface TrackingContext {
    item_list_name?: string
    item_list_id?: string
    item_size?: string
    index?: number
    quantity?: number
    promotion_id?: string
    promotion_name?: string
    forceInStock?: boolean
}

/**
 * Get tracking data for a product, using stored origin if available
 * This ensures all tracking events refer back to the original discovery point
 */
export function getTrackingDataWithOrigin(
    product: any,
    currentContext: TrackingContext,
    fallbackContext: TrackingContext = {
        item_list_name: "Product Detail Page",
        item_list_id: "pdp"
    }
): NormalizedTrackingProduct {
    // First, create a minimal tracking data just for key generation
    // Use a neutral context to get consistent item_id
    const keyGenerationData = adaptProductForTracking(product, {
        item_list_name: "temp",
        item_list_id: "temp"
    })

    const productKey = generateProductKeyFromTrackingData(keyGenerationData)
    const storedOriginData = getProductOrigin(productKey)

    if (storedOriginData) {
        // Use stored origin data for discovery context, but apply current context for other fields
        return adaptProductForTracking(product, {
            ...currentContext,
            // Use stored discovery context
            item_list_name: storedOriginData.item_list_name,
            item_list_id: storedOriginData.item_list_id,
            index: storedOriginData.index,
            // Use stored promotion data if available, otherwise use current context
            promotion_id: storedOriginData.promotion_id || currentContext.promotion_id,
            promotion_name:
                storedOriginData.promotion_name || currentContext.promotion_name
        })
    }

    // Fallback to current context
    const contextToUse =
        Object.keys(currentContext).length > 0 ? currentContext : fallbackContext
    return adaptProductForTracking(product, contextToUse)
}

/**
 * Get tracking data for multiple products, using stored origins where available
 */
export function getMultipleTrackingDataWithOrigin(
    products: any[],
    currentContext: TrackingContext,
    fallbackContext: TrackingContext = {
        item_list_name: "Product Detail Page",
        item_list_id: "pdp"
    }
): NormalizedTrackingProduct[] {
    return products.map((product, index) => {
        const contextWithIndex = {
            ...currentContext,
            index
        }

        return getTrackingDataWithOrigin(product, contextWithIndex, fallbackContext)
    })
}
